/**
 * SkyPointLogin — tenant-signup form for the Skypoint platform.
 * NOT local credential authentication for Sky Advisor.
 *
 * Sky Advisor auth is B2C-only (see server/middleware/auth.ts and
 * server/routes/auth.ts — direct /login + /register return HTTP 410
 * "AUTH_METHOD_REMOVED"). This component POSTs to an external Skypoint
 * platform endpoint (/api/user/skypointsignup, not implemented in this
 * repo) to create a new tenant; it does NOT log a user into this
 * application. The button label reads "Sign Up" (line ~138) and the
 * header copy confirms the tenant-creation intent ("Sign Up with
 * Skypoint — Enter your existing Skypoint Login details to create a
 * new tenant").
 *
 * If you are looking for local-credentials authentication: it does
 * not exist. Auth flows through B2C only. Day-40 investigation
 * confirmed this exhaustively.
 */
import { useState, useEffect } from "react";
import "../css/signup.css";
import { FormProvider } from "react-hook-form";
import { useZodForm } from "../lib/form-utils";
import { skypointLoginSchema, SkypointLoginFormData } from "../schemas/skypoint-login-schema";
import { FormTextField } from "./forms/FormTextField";
import Loader from "./shared/loader";
import { ROUTES } from "./shared/constant";
import { useNavigate, useSearchParams } from "react-router";

const SkyPointLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [planId, setPlanId] = useState("");
  const [token, setToken] = useState("");

  const methods = useZodForm({
    schema: skypointLoginSchema,
    defaultValues: {
      email: "",
      password: "",
      companyName: "",
    },
  });

  useEffect(() => {
    const planIdParam = searchParams.get("planid");
    const tokenParam = searchParams.get("token");

    if (!planIdParam) {
      setError("Invalid request. Please select a subscription plan.");
    } else {
      setPlanId(planIdParam);
      setToken(encodeURIComponent(tokenParam || ""));
    }
  }, [searchParams]);

  const backToSignup = () => {
    const searchParam = window.location.search;
    navigate({
      pathname: ROUTES.SIGNUP,
      search: searchParam,
    });
  };

  const handleSubmit = async (data: SkypointLoginFormData) => {
    setIsLoading(true);
    setError("");

    const userModel = {
      email: data.email,
      password: data.password,
      companyName: data.companyName,
      planId,
      token,
    };

    try {
      const response = await fetch(import.meta.env.VITE_API_BASE_URL + "/api/user/skypointsignup", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userModel),
      });

      const result = await response.json();
      setIsLoading(false);

      if (result.isSuccessful) {
        setIsCompleted(true);
      } else {
        handleError();
      }
    } catch (err) {
      setIsLoading(false);
      handleError();
      console.log(err);
    }
  };

  const handleError = () => {
    // Normalize error messages to prevent username enumeration
    // Don't expose backend error messages that could reveal user existence
    setError("Something went wrong please try again.");
  };

  const renderConfirmation = () => {
    return (
      <div id="signup-complete">
        <h1>Thanks for signing up.</h1>
        <h3>Please log in to appliation to setup your account.</h3>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <FormProvider {...methods}>
        <form
          id="createuser"
          onSubmit={methods.handleSubmit(handleSubmit)}
          className="signup-form"
        >
          <FormTextField
            name="email"
            type="email"
            className="form-input-control-custom skypoint-login-input"
            placeholder="Work email"
            required
          />

          <FormTextField
            name="password"
            type="password"
            className="form-input-control-custom skypoint-login-input"
            placeholder="Password"
            required
          />

          <FormTextField
            name="companyName"
            className="form-input-control-custom skypoint-login-input"
            placeholder="Company Name"
            required
          />

          <div className="buttons">
            <button
              id="btnloginsubmit"
              type="submit"
              className="btn-submit-custom"
            >
              Sign Up
            </button>
            <button
              type="button"
              className="btn-cancel-custom"
              onClick={backToSignup}
            >
              Cancel
            </button>
          </div>
        </form>
      </FormProvider>
    );
  };

  return (
    <div>
      <div className="main-div">
        <div className="sub-div">
          <div className="left-div"></div>
          <img
            className="left-div-image"
            src="https://sicdpstorage.blob.core.windows.net/sicdpb2c/assets/images/background-image.jpg"
            alt="image"
          />
          <img
            className="skypointcloud-logo"
            src="https://sicdpstorage.blob.core.windows.net/sicdpb2c/assets/images/skypoint-full-logo-white.png/"
          />
          <span className="bring-peopel-and-data">Bring People and Data Together</span>{" "}
          <span className="subtitle">
            Schedule a complimentary demo with our specialists to discover how Skypoint's AI
            Platform seamlessly integrates your data landscape, unleashing the capabilities of
            generative AI to deliver precise, industry-tailored insights throughout your
            organization.
          </span>
          <button
            className="demo-button"
            type="button"
          >
            <a
              href="https://skypointcloud.com/demo/"
              target="_blank"
              className="a-link"
              rel="noreferrer"
            >
              <span className="request-demo-txt"> Request Demo </span>{" "}
              <img
                src="https://sicdpstorage.blob.core.windows.net/sicdpb2c/assets/images/arrow-alt-circle-right.png"
                alt="button"
                className="btn-image"
              />{" "}
            </a>
          </button>
          <div className="signup-div-login">
            <div className="header-title">Sign Up with Skypoint </div>
            <p className="header-subtext-login">
              {" "}
              Enter your existing <b>Skypoint Login</b> details to create a new tenant.
            </p>

            <p
              id="signup-error-login"
              className="text-fg-danger page-error signup-error-plan"
              aria-hidden="true"
              style={error ? { display: "block" } : { display: "none" }}
            >
              {error}
            </p>

            {!isCompleted ? (
              <div className="form-div-login">{renderForm()}</div>
            ) : (
              renderConfirmation()
            )}
            <div
              className="loader"
              id="loader"
              style={isLoading ? { display: "block" } : { display: "none" }}
            >
              <Loader />
            </div>
          </div>
          <div className="full-line"> </div>
          <div className="question">
            {" "}
            Question? &nbsp;{" "}
            <a
              href="https://skypointcloud.com/customer-support/"
              target="_blank"
              className="a-link"
              rel="noreferrer"
            >
              Contact Support
            </a>{" "}
          </div>
          <div className="copyright">
            {" "}
            Copyright &copy; &nbsp;{" "}
            <a
              href="https://skypointcloud.com/"
              target="_blank"
              className="a-link"
              rel="noreferrer"
            >
              Skypoint
            </a>
            . All rights reserved.{" "}
          </div>
          <div className="term-privacy">
            {" "}
            <a
              href="https://skypointcloud.com/terms/"
              target="_blank"
              className="a-link"
              rel="noreferrer"
            >
              {" "}
              Terms of Use{" "}
            </a>{" "}
            &nbsp; | &nbsp;
            <a
              href="https://privacy.skypoint.ai/"
              target="_blank"
              className="a-link"
              rel="noreferrer"
            >
              {" "}
              Privacy Policy{" "}
            </a>{" "}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkyPointLogin;
